/**
 * THE CANONICAL MEMBERSHIP CLASS, DECIDED IN THE BROWSER AND SENT EXPLICITLY.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * The VAT rate on a padel booking depends on whether the players were members:
 * 7 % for members, 19 % for non-members. Until now the booking flow sent a
 * hyphen-separated free-text list (`"member - guest -  - "`) and every reader
 * re-parsed it, in three runtimes, at report time.
 *
 * Two consequences, both measured in production:
 *
 *   · The PayPal flow posts `players` to n8n, which never mapped it onto
 *     `bookings.player_membership`. EVERY PayPal padel booking — 991 of them,
 *     32.071,00 EUR — carries no membership at all and is a permanent Klärfall.
 *   · The Stripe flow sent only the single `membership` form field, never the
 *     per-player breakdown, and only started populating the column at all in
 *     mid-June 2026.
 *
 * So both flows now send ONE explicit, already-normalised field —
 * `membership_class` — alongside the raw evidence. A downstream writer no
 * longer has to parse anything to know the tax class, and the database trigger
 * `bookings_apply_membership_class()` guarantees the stored value is canonical
 * even for a writer that ignores this field entirely.
 *
 * `player_membership` is still sent, unchanged, as the EVIDENCE behind the
 * decision. It is never the decision itself any more.
 */

/** The canonical decision. `unresolved` is a Klärfall and never a rate. */
export type MembershipClass = 'member' | 'non_member' | 'unresolved';

/** One player, as `buildPlayersInfo()` in BookingPage emits them. */
export interface PlayerInfo {
  player?: number;
  membership_category?: string;
  name?: string;
  membership_type?: string;
}

const MEMBER_CATEGORIES: ReadonlySet<string> = new Set(['member', 'mitglied']);
const NON_MEMBER_CATEGORIES: ReadonlySet<string> = new Set([
  'guest', 'gast', 'student', 'studentin', 'nonmember', 'non_member', 'nichtmitglied',
]);

/**
 * Decide the class from the per-player categories.
 *
 * Mirrors `svh_classify_membership()` in the database and
 * `classifyPadelMembership()` in vatClassification.ts:
 *
 *   · an unrecognised category leaves the WHOLE booking unresolved — a player
 *     nobody can classify is not silently dropped from the group;
 *   · EVERY player a member makes it a member booking;
 *   · EVERY player a non-member makes it a non-member booking;
 *   · any MIX is unresolved — members pay nothing, so the revenue on a mixed
 *     booking is the guests', and taxing it at the member rate would assert a
 *     result SVH has not confirmed.
 *
 * An empty list is unresolved, never a default.
 */
export function canonicalMembershipClass(players: readonly PlayerInfo[]): MembershipClass {
  if (!Array.isArray(players) || players.length === 0) return 'unresolved';

  const categories = players.map(p =>
    String(p?.membership_category ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_'));

  if (categories.some(c => c === '')) return 'unresolved';

  const known = categories.every(c => MEMBER_CATEGORIES.has(c) || NON_MEMBER_CATEGORIES.has(c));
  if (!known) return 'unresolved';

  if (categories.every(c => MEMBER_CATEGORIES.has(c))) return 'member';
  if (categories.every(c => NON_MEMBER_CATEGORIES.has(c))) return 'non_member';
  return 'unresolved';
}

/**
 * The raw evidence string, in the shape the column has always held.
 *
 * Kept so nothing downstream breaks and so the stored decision can always be
 * audited against what the booker actually selected.
 */
export function playerMembershipEvidence(players: readonly PlayerInfo[]): string {
  if (!Array.isArray(players) || players.length === 0) return '';
  return players
    .map(p => String(p?.membership_category ?? '').trim().toLowerCase())
    .join(' - ');
}

/** Reads the hidden `players_json` field BookingPage renders. Never throws. */
export function readPlayersFromForm(): PlayerInfo[] {
  if (typeof document === 'undefined') return [];
  const raw = document.querySelector<HTMLInputElement>('[name="players_json"]')?.value?.trim() ?? '';
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as PlayerInfo[]) : [];
  } catch {
    return [];
  }
}

/**
 * The membership fields EVERY checkout path must send, whatever the provider.
 *
 * Stripe and PayPal now transmit the identical three keys, so the two paths
 * can no longer disagree about a tax class the way they have since April.
 */
export function membershipPayloadFields(players?: readonly PlayerInfo[]): {
  membership_class: MembershipClass;
  player_membership: string;
  players: PlayerInfo[];
} {
  const list = players ?? readPlayersFromForm();
  return {
    membership_class: canonicalMembershipClass(list),
    player_membership: playerMembershipEvidence(list),
    players: [...list],
  };
}
